import { useCallback, useContext, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { fetchProductSubscription } from 'actions/LuxPlusActions'
import FormatCurrency from 'components/Common/FormatCurrency'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { useAppDispatch } from 'hooks/reduxHooks'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LuxPlusSubscriptionCancellationModal from '../LuxPlusSubscriptionCancellationModal'
import TextLink from 'LuxKit/TextLink'
import ModalContext from 'contexts/ModalContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import LuxPlusBenefitsList from '../LuxPlusBenefitsList'
import LuxPlusSubscriptionTsAndCs from '../LuxPlusSubscriptionTsAndCs'
import { connect } from 'react-redux'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { generateLuxPlusSubscriptionItems } from 'lib/checkout/luxPlusSubscription/cart'
import { addToCartGTM } from 'analytics/eventDefinitions'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import CurrencyContext from 'contexts/currencyContext'

interface MappedProps {
  subscription: App.ProductSubscription
  isLoggedIn: boolean
}

interface Props extends MappedProps {}

function LuxPlusHiddenOfferModal(props: Props) {
  const { subscription } = props
  const intl = useIntl()
  const { resolve } = useModalElementContext<boolean>()

  const currency = useContext(CurrencyContext)
  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()

  const onShowCancellationPolicy = useCallback(() => {
    showModal(<LuxPlusSubscriptionCancellationModal offer={subscription.offers?.[0]} />)
  }, [showModal, subscription.offers])

  const closeModal = useCallback(() => resolve(true), [resolve])

  const handlePrimaryAction = useCallback(() => {
    const offerWithJoinFee = subscription?.offers?.find(offer => !offer.joinFeeWaived)
    if (offerWithJoinFee) {
      const cartItems = generateLuxPlusSubscriptionItems(offerWithJoinFee)
      fireEcommerceGTMEvent(addToCartGTM(offerWithJoinFee, currency, offerWithJoinFee.price + offerWithJoinFee.joinFee, undefined, 'subscription'))
      dispatch(addToCheckoutCart(cartItems))
      dispatch(navigateToCheckout())
      closeModal()
    }
  },
  [closeModal, currency, dispatch, subscription?.offers])

  useEffect(() => {
    dispatch(fetchProductSubscription())
  }, [dispatch])

  return <Modal
    primaryActionText={intl.formatMessage({
      id: 'FzsYCsCZ2GyL1-HN9vvn',
      defaultMessage: 'Join {programName}',
      description: 'Primary action button text to join LuxPlus+ from hidden offer modal',
    }, { programName: LUXURY_PLUS.PROGRAM_NAME })}
    onPrimaryActionClick={handlePrimaryAction}
    title={intl.formatMessage({
      id: 'foPlnyzW0JGzUEoy7THu',
      defaultMessage: 'Join {programName} to view this hidden offer',
      description: 'Modal title encouraging user to join LuxPlus+ to view a hidden offer',
    }, { programName: LUXURY_PLUS.PROGRAM_NAME })}
  >
    {subscription.fetching && <LoadingIndicator />}
    {subscription.error && <BodyText variant="medium">{intl.formatMessage({
      id: 'pG0-YTlqzXbSRJDrmFgF',
      defaultMessage: 'There was an error loading the details for the program. Please try again later.',
      description: 'Error message when LuxPlus+ subscription details fail to load in hidden offer modal',
    })}
    </BodyText>}
    {subscription.offers &&
      <Group direction="vertical" gap={24}>
        <Group direction="vertical" gap={12}>
          <BodyText variant="medium">
            <FormattedMessage
              id="PWKbPjhu0j4YMXlkLVB6"
              defaultMessage="Become a member of our VIP travel club for just {price}/year to unlock this hidden hotel offer today."
              description="Body text encouraging user to join the VIP travel club at a given yearly price to unlock a hidden hotel offer"
              values={{ price: <FormatCurrency value={subscription.offers[0].price} /> }}
            />
          </BodyText>
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: 'Xl0RqMDLeSVIdSoHhN55',
              defaultMessage: 'You’ll also enjoy these unlimited benefits all year round:',
              description: 'Section heading for unlimited yearly benefits in LuxPlus+ hidden offer modal',
            })}
          </BodyText>
          <LuxPlusBenefitsList kind="long" benefits={subscription.offers[0].benefits} />
          <TextLink weight="bold" colour="highlight-primary-dark" size="medium" endIcon={<LineInfoCircleIcon colour="normal-contrast" />} onClick={onShowCancellationPolicy}>
            {intl.formatMessage({
              id: 'mFqKa20v7jurvrmIGDpf',
              defaultMessage: 'Cancel renewal anytime',
              description: 'Link text to view cancellation policy in LuxPlus+ hidden offer modal',
            })}
          </TextLink>
        </Group>
        <LuxPlusSubscriptionTsAndCs onTsAndCsLinkClick={closeModal} />
      </Group>}
  </Modal>
}

const mapStateToProps = (state: App.State): MappedProps => ({
  subscription: state.luxPlus.products.subscription,
  isLoggedIn: selectLoggedIn(state),
})

export default connect(mapStateToProps)(LuxPlusHiddenOfferModal)
